import { Helmet } from "react-helmet-async";
import { COMPANY } from "@/lib/site";

const DEFAULT_IMG = "https://images.pexels.com/photos/17245900/pexels-photo-17245900.jpeg";

export const SEO = ({
  title,
  description = `Production powder coating, media blasting, and chemical stripping in ${COMPANY.city}. ${COMPANY.region}. Parts up to ${COMPANY.maxLength} × ${COMPANY.maxWidth}. Fast turnaround, documented QC.`,
  image = DEFAULT_IMG,
  path = "",
  jsonLd,
}) => {
  const fullTitle = title ? `${title} — ${COMPANY.name}` : `${COMPANY.name} · Industrial Powder Coating in ${COMPANY.city}`;
  const url = typeof window !== "undefined" ? window.location.origin + path : path;
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {/* Open Graph — Facebook, LinkedIn, iMessage, etc. */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={COMPANY.name} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
};

export const localBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": "https://americanarmorcoatings.com/#business",
  name: COMPANY.name,
  url: "https://americanarmorcoatings.com",
  telephone: COMPANY.phone,
  email: COMPANY.email,
  image: DEFAULT_IMG,
  priceRange: "$$",
  address: {
    "@type": "PostalAddress",
    addressLocality: COMPANY.address.locality,
    addressRegion: COMPANY.address.region,
    postalCode: COMPANY.address.postal,
    addressCountry: COMPANY.address.country,
  },
  areaServed: [
    { "@type": "State", name: "Tennessee" },
    { "@type": "State", name: "Kentucky" },
    { "@type": "State", name: "Alabama" },
    { "@type": "State", name: "Georgia" },
  ],
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    opens: "08:30",
    closes: "18:00",
  },
  sameAs: [
    COMPANY.social.facebook,
    COMPANY.social.instagram,
    COMPANY.social.tiktok,
  ],
});

export const imageGallerySchema = (album, items, siteUrl) => ({
  "@context": "https://schema.org",
  "@type": "ImageGallery",
  name: album?.title || "Gallery",
  description: album?.description || `Powder coating, blasting, and finishing work by ${COMPANY.name}.`,
  url: album ? `${siteUrl}/gallery/${album.slug}` : `${siteUrl}/gallery`,
  associatedMedia: (items || []).map(it => ({
    "@type": "ImageObject",
    contentUrl: it.image_url?.startsWith("http") ? it.image_url : `${siteUrl}${it.image_url}`,
    name: it.title,
    caption: it.description || it.tag,
  })),
});
